/**
 * Tile spec resolution: a stored query string becomes everything needed to
 * fetch and draw one tile.
 *
 * **Pure.** No atoms are read here and none are written. That is the whole point
 * - `plotState`, the filter atoms and the date atoms are single globals shared
 * with Explore, so a dashboard that resolved tiles through them could only ever
 * draw one tile at a time. Every parser this needs (`parsePlotState`,
 * `parseFiltersParam`, `parseDerivedParam`, `parseOutliersParam`) was already
 * pure and exported; this module just composes them.
 *
 * Context resolution is the variable model (`variables.ts`): a tile either
 * references the dashboard's `ship` / `window` variables - the ordinary case,
 * and what the toolbar feeds - or pins a literal of its own. `SharedContext` is
 * kept as a name for the resolved values so the grid and tile components read
 * the same as before.
 */

import { describeTable } from '../plot/table-config.ts';
import { describeGauge } from '../plot/gauge-config.ts';
import { describeSankey } from '../plot/sankey-config.ts';
import { defaultPlotState, type PlotState } from '../../state/plot-state.ts';
import {
  parsePlotState,
  parseFiltersParam,
  parseDerivedParam,
  parseOutliersParam,
} from '../../state/url-sync.ts';
import { DEFAULT_OUTLIERS } from '../../state/store.ts';
import type { ShapingSource } from '../../state/request.ts';
import { ROLLING_PRESET_MINUTES, presetWindow } from '../../utils/dates.ts';
import { shipLabel } from '../../utils/ships.ts';
import type { DashboardTile } from './dashboard-state.ts';
import {
  isRef,
  LIVE_WINDOW_VAR,
  refName,
  resolveWindow,
  WINDOW_VAR,
  type TimeWindow,
  type VariableValues,
} from './variables.ts';

/**
 * Resolved variable values - what a tile inherits when it does not pin - and
 * which sources keep no history (Live), whose tiles follow the Live window
 * instead of the dashboard's. Passed in rather than read, to stay pure.
 */
export type SharedContext = VariableValues & { liveSources?: readonly string[] };

/** Everything one tile needs, with nothing left to look up. */
export interface ResolvedTile {
  /** The tile's own data source - never Explore's. */
  source: string;
  state: PlotState;
  /** Anchor first, as `plottedShipIds()` orders them. */
  ships: string[];
  start: string;
  end: string;
  shaping: ShapingSource;
  /**
   * The rolling preset the window came from ("10m"), or null for a fixed one.
   * A rolling window moves every time it is resolved, so this - not `start` /
   * `end` - is what says the tile's recipe changed; and a Live tile on one
   * ticks.
   */
  rolling: string | null;
  /**
   * True when the tab has slicer selections this tile could not honour - its
   * own filters are joined with OR, or it is a legacy expression-only tile. The
   * tile says so rather than quietly showing more than was asked for.
   */
  slicersUnapplied: boolean;
}

/**
 * Resolve a preset name to a concrete window, or null when it cannot be
 * resolved without atoms. The same `presetWindow` the sidebar applies, so every
 * preset - calendar ones included - means the same thing on a tile.
 *
 * `max` returns null deliberately: it means "this ship's whole range", which
 * is a per-ship metadata lookup, not arithmetic - the caller falls back to the
 * shared window rather than inventing one.
 */
export function resolvePreset(preset: string): { start: string; end: string } | null {
  return presetWindow(preset);
}

/**
 * The ships this tile draws: a referenced variable's value, or its own pin.
 *
 * An empty pin falls back to the shared value rather than drawing nothing - a
 * tile whose ship was deleted is still a chart, it just needs a subject.
 */
function tileShips(tile: DashboardTile, shared: VariableValues): string[] {
  const bind = tile.bind.ship;
  if (isRef(bind)) {
    const value = shared.byId[refName(bind)];
    const ships = Array.isArray(value) ? value : shared.ships;
    return ships.length ? ships : shared.ships;
  }
  return bind.length ? bind : shared.ships;
}

/** The window this tile draws as stored, or null to inherit the shared one. */
function tileWindowSpec(tile: DashboardTile, shared: SharedContext): TimeWindow | null {
  const bind = tile.bind.window;
  if (isRef(bind)) {
    // A Live tile on the dashboard's time reads the Live window instead.
    const name =
      refName(bind) === WINDOW_VAR && shared.liveSources?.includes(tile.source) ? LIVE_WINDOW_VAR : refName(bind);
    const value = shared.byId[name];
    return value && !Array.isArray(value) ? (value as TimeWindow) : null;
  }
  return bind;
}

/** Filters, derived signals and outliers as stored on the tile's own query. */
export function tileShaping(params: URLSearchParams): ShapingSource {
  const src: ShapingSource = {};
  const filters = params.get('filters');
  if (filters) src.filterRows = parseFiltersParam(filters);
  const logic = params.get('logic');
  if (logic === 'OR' || logic === 'AND') src.logic = logic;
  const expr = params.get('fexpr');
  if (expr) src.expr = expr;
  // A tile keeps meaning what it meant when saved: `and` is today's one list,
  // `expr` the old expression-only switch, anything else rows only.
  const fmode = params.get('fmode');
  src.mode = fmode === 'and' ? 'and' : fmode === 'expr' ? 'expr' : 'builder';
  const derived = params.get('d');
  if (derived) src.derived = parseDerivedParam(derived);
  const ol = params.get('ol');
  src.outliers = ol ? parseOutliersParam(ol) : { ...DEFAULT_OUTLIERS };
  return src;
}

/**
 * Whether a tile's own filters leave no room for a slicer. A slicer must narrow,
 * and under OR it would widen; an expression-only tile sends no filter rows.
 */
function excludesSlicers(shaping: ShapingSource): boolean {
  if (shaping.mode === 'expr') return true;
  const own = (shaping.filterRows ?? []).some((r) => !r.slicer && r.column);
  return own && shaping.logic === 'OR';
}

export function resolveTile(tile: DashboardTile, shared: SharedContext): ResolvedTile {
  const params = new URLSearchParams(tile.query);
  const state = parsePlotState(params, defaultPlotState());
  const spec = tileWindowSpec(tile, shared);
  const window = resolveWindow(spec);
  const rolling = spec?.preset && ROLLING_PRESET_MINUTES[spec.preset] !== undefined ? spec.preset : null;
  const shaping = tileShaping(params);
  const active = tile.ignoreSlicers ? [] : (shared.slicers ?? []);
  const blocked = active.length > 0 && excludesSlicers(shaping);
  if (active.length && !blocked) {
    shaping.slicers = active.map(({ id: _id, ...rest }) => rest);
  }

  return {
    source: tile.source,
    state,
    ships: tileShips(tile, shared),
    start: window?.start ?? shared.start,
    end: window?.end ?? shared.end,
    shaping,
    rolling,
    slicersUnapplied: blocked,
  };
}

/** Guard legacy/imported tiles that still carry deferred period context. */
export function tileHasPeriodComparison(tile: DashboardTile): boolean {
  return new URLSearchParams(tile.query).has('pc') || !!tile.bind.extra?.pc;
}

/**
 * The query string that reopens this tile in Explore.
 *
 * Context is re-attached from wherever the tile actually got it, so what opens
 * is the chart the tile is showing - not the recipe with somebody else's ship.
 */
export function tileExploreQuery(tile: DashboardTile, shared: SharedContext): string {
  const resolved = resolveTile(tile, shared);
  const params = new URLSearchParams(tile.query);
  params.set('tab', 'plot');
  const [anchor, ...extras] = resolved.ships;
  if (anchor) params.set('ship', anchor);
  if (extras.length) params.set('ships', extras.join(','));
  params.set('start', resolved.start);
  params.set('end', resolved.end);
  if (!isRef(tile.bind.window) && tile.bind.window.preset) {
    params.set('preset', tile.bind.window.preset);
  } else if (resolved.rolling) {
    // A Live tile on the Live window opens rolling, so it ticks in Explore too.
    params.set('preset', resolved.rolling);
  }
  const pc = tile.bind.extra?.pc;
  if (pc) params.set('pc', pc);
  return params.toString();
}

/**
 * The X column a recipe names. An absent `x` param means the default time
 * column, which `appendPlotConfig` omits - so reading it as "?" would label
 * every ordinary time-series tile as broken.
 */
function xLabel(p: URLSearchParams): string {
  return p.get('x') || 'time';
}

/**
 * A one-line description of what a tile draws, for its header.
 *
 * Reads the recipe rather than the fetched response so it is available before
 * anything loads - the header should say what the tile is while it is still
 * queueing.
 */
export function tileSummary(tile: DashboardTile): string {
  const p = new URLSearchParams(tile.query);
  const pt = p.get('pt') || 'line';
  const parts: string[] = [pt];
  if (pt === 'table') {
    parts.push(describeTable(p));
  } else if (pt === 'gauge') {
    parts.push(describeGauge(p));
  } else if (pt === 'sankey') {
    parts.push(describeSankey(p));
  } else if (pt === 'number') {
    parts.push(p.get('nlabel') || p.get('nexpr') || `${p.get('nagg') || 'latest'}(${p.get('ncol') || '?'})`);
  } else if (pt === 'histogram') {
    const hc = (p.get('hc') || '').split(',').filter(Boolean);
    parts.push(hc.length ? hc.join(', ') : 'no columns');
  } else if (pt === 'heatmap') {
    parts.push(`${p.get('hy') || '?'} vs ${xLabel(p)}`);
  } else if (pt === 'correlation') {
    const cc = (p.get('cc') || '').split(',').filter(Boolean);
    parts.push(cc.length ? `${cc.length} signals` : 'no signals');
  } else if (pt === 'pie') {
    if (p.get('pmo') === 'bands') {
      parts.push(`${p.get('pbc') || '?'} bands`);
    } else {
      const cols = (p.get('pcol') || '').split(',').filter(Boolean);
      parts.push(cols.length ? `${cols.length} signals` : 'no signals');
    }
  } else if (pt === 'box') {
    if (p.get('bmo') === 'groups') {
      parts.push(`${p.get('bcol') || '?'} by ${p.get('bgrp') || '?'}`);
    } else {
      const cols = (p.get('bcols') || '').split(',').filter(Boolean);
      parts.push(cols.length ? `${cols.length} signals` : 'no signals');
    }
  } else if (pt === 'map') {
    parts.push('track');
  } else if (pt === 'scatter') {
    const y = (p.get('yl') || '').split(',').filter(Boolean);
    parts.push(`${y.length ? y.join(', ') : '?'} vs ${xLabel(p)}`);
  } else {
    const cols = [...(p.get('yl') || '').split(','), ...(p.get('yr') || '').split(',')].filter(
      Boolean,
    );
    parts.push(cols.length ? cols.join(', ') : 'no series');
  }
  // A tile names whichever dimensions differ from the toolbar above it.
  const pinned = isRef(tile.bind.ship) ? [] : tile.bind.ship;
  const anchor = pinned[0];
  if (anchor) {
    const extra = pinned.length - 1;
    const label = extra > 0 ? `${shipLabel(anchor)} +${extra}` : shipLabel(anchor);
    const window = isRef(tile.bind.window) ? null : tile.bind.window;
    parts.push(`${label} · ${window ? window.preset || 'custom range' : 'dashboard time'}`);
  } else if (!isRef(tile.bind.window)) {
    parts.push(`dashboard ships · ${tile.bind.window.preset || 'custom range'}`);
  }
  return parts.join(' · ');
}

/** A default tile title from the recipe, so a captured chart is never "Chart". */
export function suggestTileTitle(query: string): string {
  const p = new URLSearchParams(query);
  const pt = p.get('pt') || 'line';
  if (pt === 'number') return p.get('nlabel') || p.get('nexpr') || (p.get('ncol') ? `${(p.get('nagg') || 'latest').toUpperCase()}(${p.get('ncol')})` : 'Number');
  if (pt === 'histogram') {
    const hc = (p.get('hc') || '').split(',').filter(Boolean);
    return hc.length ? hc.join(', ') : 'Histogram';
  }
  if (pt === 'map') return 'Track';
  if (pt === 'table') return describeTable(p);
  if (pt === 'gauge') return describeGauge(p);
  if (pt === 'sankey') return describeSankey(p);
  if (pt === 'correlation') {
    const cc = (p.get('cc') || '').split(',').filter(Boolean);
    return cc.length ? `${cc.join(', ')} correlation` : 'Correlation';
  }
  if (pt === 'pie') {
    if (p.get('pmo') === 'bands') {
      const col = p.get('pbc');
      return col ? `${col} by band` : 'Pie';
    }
    const cols = (p.get('pcol') || '').split(',').filter(Boolean);
    return cols.length ? `Share of ${cols.join(', ')}` : 'Pie';
  }
  if (pt === 'box') {
    if (p.get('bmo') === 'groups') {
      const col = p.get('bcol');
      const grp = p.get('bgrp');
      return col && grp ? `${col} by ${grp}` : 'Box plot';
    }
    const cols = (p.get('bcols') || '').split(',').filter(Boolean);
    return cols.length ? `Spread of ${cols.join(', ')}` : 'Box plot';
  }
  if (pt === 'heatmap') {
    const y = p.get('hy');
    return y ? `${y} vs ${xLabel(p)}` : 'Heatmap';
  }
  const cols = [...(p.get('yl') || '').split(','), ...(p.get('yr') || '').split(',')].filter(
    Boolean,
  );
  if (!cols.length) return pt === 'scatter' ? 'Scatter' : 'Chart';
  if (pt === 'scatter') return `${cols.join(', ')} vs ${xLabel(p)}`;
  return cols.join(', ');
}
